// approver-onboarding-storage.ts
export interface ApproverOnboardingProgress {
  userId: string
  companyId: string
  currentStep: number
  hasCompleted: boolean
  departmentName: string
  lastUpdated: string
}

const STORAGE_KEY = 'vtApproverOnboardingProgress'

// Custom event name to notify listeners within the same tab
export const APPROVER_ONBOARDING_PROGRESS_EVENT = 'vt:approverOnboardingProgressUpdated'

const emitApproverOnboardingProgressUpdated = (progress: ApproverOnboardingProgress | null) => {
  try {
    window.dispatchEvent(new CustomEvent(APPROVER_ONBOARDING_PROGRESS_EVENT, { detail: progress }))
  } catch {
    // no-op in non-browser environments
  }
}

export const getApproverOnboardingProgress = (userId: string, companyId: string): ApproverOnboardingProgress | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const allProgress = JSON.parse(stored)
    const userKey = `${userId}_${companyId}`

    if (allProgress[userKey]) {
      return allProgress[userKey]
    }

    return null
  } catch (error) {
    console.error('Error reading approver onboarding progress from localStorage:', error)
    return null
  }
}

export const saveApproverOnboardingProgress = (
  userId: string,
  companyId: string,
  currentStep: number,
  departmentName: string,
  hasCompleted = false
): void => {
  try {
    const userKey = `${userId}_${companyId}`
    const stored = localStorage.getItem(STORAGE_KEY)
    let allProgress = {}

    if (stored) {
      allProgress = JSON.parse(stored)
    }

    const progress: ApproverOnboardingProgress = {
      userId,
      companyId,
      currentStep,
      hasCompleted,
      departmentName,
      lastUpdated: new Date().toISOString(),
    }

    allProgress[userKey] = progress
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allProgress))
    emitApproverOnboardingProgressUpdated(progress)
  } catch (error) {
    console.error('Error saving approver onboarding progress to localStorage:', error)
  }
}

export const markApproverOnboardingComplete = (userId: string, companyId: string): void => {
  try {
    const userKey = `${userId}_${companyId}`
    const stored = localStorage.getItem(STORAGE_KEY)
    let allProgress = {}

    if (stored) {
      allProgress = JSON.parse(stored)
    }

    const existingProgress = allProgress[userKey]

    if (existingProgress) {
      const progress: ApproverOnboardingProgress = {
        ...existingProgress,
        hasCompleted: true,
        lastUpdated: new Date().toISOString(),
      }

      allProgress[userKey] = progress
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allProgress))
      emitApproverOnboardingProgressUpdated(progress)
    }
  } catch (error) {
    console.error('Error marking approver onboarding as complete:', error)
  }
}

export const initializeApproverOnboardingProgress = (
  userId: string,
  companyId: string,
  departmentName: string
): void => {
  try {
    const existingProgress = getApproverOnboardingProgress(userId, companyId)

    // Only initialize if no progress exists
    if (!existingProgress) {
      const userKey = `${userId}_${companyId}`
      const stored = localStorage.getItem(STORAGE_KEY)
      let allProgress = {}

      if (stored) {
        allProgress = JSON.parse(stored)
      }

      const progress: ApproverOnboardingProgress = {
        userId,
        companyId,
        currentStep: 0, // Start at welcome step
        hasCompleted: false,
        departmentName,
        lastUpdated: new Date().toISOString(),
      }

      allProgress[userKey] = progress
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allProgress))
      emitApproverOnboardingProgressUpdated(progress)
    }
  } catch (error) {
    console.error('Error initializing approver onboarding progress:', error)
  }
}

export const shouldShowApproverOnboardingProgress = (
  progress: ApproverOnboardingProgress | null,
  isUserApprover: boolean,
  companyPlan?: string
): boolean => {
  // Never show onboarding for Freemium plans
  if (companyPlan === 'Freemium') {
    return false
  }

  if (!progress) return false

  // If user is no longer an approver, return false (should not show)
  if (!isUserApprover) return false

  // Show if onboarding is not completed (even if they're still on step 0 - welcome screen)
  return !progress.hasCompleted
}

export const dismissApproverOnboarding = (userId: string, companyId: string): void => {
  try {
    const userKey = `${userId}_${companyId}`
    const stored = localStorage.getItem(STORAGE_KEY)
    let allProgress = {}

    if (stored) {
      allProgress = JSON.parse(stored)
    }

    const existingProgress = allProgress[userKey]

    const progress: ApproverOnboardingProgress = {
      userId,
      companyId,
      currentStep: existingProgress?.currentStep || 0,
      hasCompleted: true, // Mark as completed when dismissed
      departmentName: existingProgress?.departmentName || '',
      lastUpdated: new Date().toISOString(),
    }

    allProgress[userKey] = progress
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allProgress))
    emitApproverOnboardingProgressUpdated(progress)
  } catch (error) {
    console.error('Error dismissing approver onboarding:', error)
  }
}

/**
 * Clear approver onboarding progress for a user
 * This should be called when a user is no longer an approver
 */
export const clearApproverOnboardingProgress = (userId: string, companyId: string): void => {
  try {
    const userKey = `${userId}_${companyId}`
    const stored = localStorage.getItem(STORAGE_KEY)

    if (!stored) return

    const allProgress = JSON.parse(stored)

    // Delete the user's progress
    if (allProgress[userKey]) {
      delete allProgress[userKey]
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allProgress))
      emitApproverOnboardingProgressUpdated(null)
    }
  } catch (error) {
    console.error('Error clearing approver onboarding progress:', error)
  }
}